import { looksLikeVideo } from "./utils";
import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { loadFFmpeg, fetchFile, ffmpegOutputToBlob, frameWithFfmpeg } from "./operations";
import JSZip from "jszip";
import { parseTime } from "./timeParse";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a thumbnail image from a specific frame of a video, useful for creating a preview image without needing to take a separate screenshot or open a video editor — a common need for uploading a video somewhere that requires its own thumbnail, or for a preview image on a page linking to the video.`,
  how: `Upload your video, find the frame you want (step buttons, a time box, or the Suggest frames strip), choose a size such as 1280 x 720 for YouTube, add a title if you like, and capture. Download one image or all of them as a ZIP.`,
  tip: `Pick a frame with a clear, in-focus subject and good lighting — a thumbnail pulled from a blurry transition or motion-heavy moment rarely looks good on its own, even if that exact moment matters in the video itself. Scrubbing slowly near a scene change usually finds a sharper frame than scrubbing quickly and guessing.`,
};

const SIZES = [
  { id: "orig", label: "Original size", w: 0, h: 0 },
  { id: "yt4k", label: "YouTube 4K 3840 x 2160 (recommended)", w: 3840, h: 2160 },
  { id: "yt", label: "YouTube HD 1280 x 720", w: 1280, h: 720 },
  { id: "short4k", label: "YouTube Shorts 4K 2160 x 3840 (recommended)", w: 2160, h: 3840 },
  { id: "vert", label: "Shorts / Reels 1080 x 1920", w: 1080, h: 1920 },
  { id: "sq", label: "Square 1080 x 1080", w: 1080, h: 1080 },
] as const;

const fmtTime = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(2).padStart(5, "0")}`;

interface Shot { url: string; name: string; time: number; blob: Blob }

const stamp = (t: number) => {
  const m = Math.floor(t / 60), sec = (t % 60).toFixed(2).padStart(5, "0");
  return `${String(m).padStart(2, "0")}m${sec}s`;
};

const FONTS = [
  { id: "sans", label: "Bold sans", css: '800 {px}px "Noto Sans", "Noto Sans Devanagari", system-ui, sans-serif' },
  { id: "serif", label: "Serif", css: '700 {px}px "Noto Serif", Georgia, "Noto Sans Devanagari", serif' },
  { id: "impact", label: "Impact style", css: '900 {px}px Impact, "Arial Black", "Noto Sans", "Noto Sans Devanagari", sans-serif' },
  { id: "mono", label: "Monospace", css: '700 {px}px ui-monospace, Menlo, Consolas, monospace' },
] as const;
const TITLE_COLORS = [{ id: "#ffffff", label: "White" }, { id: "#ffdd00", label: "Yellow" }, { id: "#ff4040", label: "Red" }, { id: "#000000", label: "Black" }];

export default function ThumbnailGen() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [shots, setShots] = useState<Shot[]>([]);
  const [asJpg, setAsJpg] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [sizeId, setSizeId] = useState<(typeof SIZES)[number]["id"]>("orig");
  const [fitMode, setFitMode] = useState<"fill" | "fit">("fill");
  const [title, setTitle] = useState("");
  const [fontId, setFontId] = useState<(typeof FONTS)[number]["id"]>("sans");
  const [titleColor, setTitleColor] = useState("#ffffff");
  const [titleScale, setTitleScale] = useState(9);
  const [timeText, setTimeText] = useState("");
  const [candidates, setCandidates] = useState<{ t: number; url: string; score: number; best: boolean }[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [noPlayback, setNoPlayback] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`); return; }
    setFile(f); setPreview(URL.createObjectURL(f)); setShots([]); setCandidates([]); setNoPlayback(false); setTimeText("");
  }, []);

  const step = (dt: number) => {
    const v = videoRef.current; if (!v) return;
    v.pause();
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + dt));
  };

  /** Draws a source (video or picture) at the chosen size, with the optional title. */
  const compose = (src: CanvasImageSource, sw: number, sh: number): HTMLCanvasElement => {
    const size = SIZES.find((x) => x.id === sizeId)!;
    const W = size.w || sw, H = size.h || sh;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d")!;
    if (!size.w) ctx.drawImage(src, 0, 0);
    else if (fitMode === "fill") {
      const k = Math.max(W / sw, H / sh), dw = sw * k, dh = sh * k;
      ctx.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
    } else {
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      const k = Math.min(W / sw, H / sh), dw = sw * k, dh = sh * k;
      ctx.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
    }
    const text = title.trim();
    if (text) {
      const fs = Math.round(H * titleScale / 100);
      ctx.font = FONTS.find((f) => f.id === fontId)!.css.replace("{px}", String(fs));
      ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
      // Break into lines that fit 90% of the width.
      const words = text.split(/\s+/), lines: string[] = [];
      let cur = "";
      for (const w of words) { const t = cur ? `${cur} ${w}` : w; if (ctx.measureText(t).width > W * 0.9 && cur) { lines.push(cur); cur = w; } else cur = t; }
      if (cur) lines.push(cur);
      const y0 = H - H * 0.06 - (lines.length - 1) * fs * 1.15;
      ctx.lineJoin = "round"; ctx.lineWidth = fs * 0.16; ctx.strokeStyle = titleColor === "#000000" ? "#fff" : "#000"; ctx.fillStyle = titleColor;
      lines.forEach((l, i) => { const y = y0 + i * fs * 1.15; ctx.strokeText(l, W / 2, y); ctx.fillText(l, W / 2, y); });
    }
    return canvas;
  };

  const finish = (canvas: HTMLCanvasElement, time: number) => {
    canvas.toBlob((blob) => {
      if (!blob || !file) return;
      const ext = asJpg ? "jpg" : "png";
      const tag = `${sizeId !== "orig" ? `_${sizeId}` : ""}${title.trim() ? "_title" : ""}`;
      const base = `${file.name.replace(/\.[^.]+$/, "")}_${stamp(time)}${tag}`;
      setShots((prev) => {
        const taken = new Set(prev.map((x) => x.name));
        let name = `${base}.${ext}`, k = 2;
        while (taken.has(name)) name = `${base}-${k++}.${ext}`;
        return [...prev, { url: URL.createObjectURL(blob), name, time, blob }];
      });
      toast.success(`Captured at ${time.toFixed(2)} s (${canvas.width} x ${canvas.height} px)`);
    }, asJpg ? "image/jpeg" : "image/png", 0.92);
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!file) return;
    if (noPlayback) {
      // The browser cannot play this video: ffmpeg reads the frame instead.
      const t = parseTime(timeText);
      if (!(t >= 0)) { toast.error("Type the time of the frame, for example 0:05.5"); return; }
      setBusy(true);
      try {
        const ff = await loadFFmpeg(() => {});
        const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
        await ff.writeFile(`input.${ext}`, await fetchFile(file));
        const png = await frameWithFfmpeg(ff, `input.${ext}`, t);
        const bmp = await createImageBitmap(ffmpegOutputToBlob(png, "image/png"));
        finish(compose(bmp, bmp.width, bmp.height), t);
      } catch { toast.error("Could not read that frame from the video"); } finally { setBusy(false); }
      return;
    }
    if (!v) return;
    finish(compose(v, v.videoWidth, v.videoHeight), v.currentTime);
  };

  const seekTo = (t: number) => { const v = videoRef.current; if (!v) return; v.pause(); v.currentTime = Math.max(0, Math.min(v.duration || 0, t)); };

  /** Picks 10 moments across the video and marks the sharpest bright one, skipping black and blurry frames. */
  const suggest = async () => {
    if (!preview) return;
    setSuggesting(true); setCandidates([]);
    try {
      const v = document.createElement("video");
      v.src = preview; v.muted = true; v.preload = "auto";
      await new Promise<void>((res, rej) => { v.onloadeddata = () => res(); v.onerror = () => rej(new Error("play")); });
      const dur = v.duration || 0;
      const out: { t: number; url: string; score: number; best: boolean }[] = [];
      const c = document.createElement("canvas"); const cw = 192, ch = Math.max(1, Math.round((192 * v.videoHeight) / Math.max(1, v.videoWidth)));
      c.width = cw; c.height = ch;
      const ctx = c.getContext("2d", { willReadFrequently: true })!;
      for (let i = 0; i < 10; i++) {
        const t = dur * ((i + 0.5) / 10);
        await new Promise<void>((res) => { v.onseeked = () => res(); v.currentTime = t; });
        ctx.drawImage(v, 0, 0, cw, ch);
        const d = ctx.getImageData(0, 0, cw, ch).data;
        const g = new Float32Array(cw * ch); let mean = 0;
        for (let k = 0; k < g.length; k++) { g[k] = 0.299 * d[k * 4] + 0.587 * d[k * 4 + 1] + 0.114 * d[k * 4 + 2]; mean += g[k]; }
        mean /= g.length;
        let lap = 0, n = 0;
        for (let y = 1; y < ch - 1; y++) for (let x = 1; x < cw - 1; x++) { const i0 = y * cw + x; const l = 4 * g[i0] - g[i0 - 1] - g[i0 + 1] - g[i0 - cw] - g[i0 + cw]; lap += l * l; n++; }
        const sharp = n ? lap / n : 0;
        out.push({ t, url: c.toDataURL("image/jpeg", 0.7), score: mean < 20 || mean > 240 ? -1 : sharp, best: false });
      }
      const top = out.reduce((a, b) => (b.score > a.score ? b : a), out[0]);
      if (top && top.score >= 0) top.best = true;
      setCandidates(out);
    } catch { toast.error("This video can't be previewed in your browser, so frames can't be suggested."); }
    finally { setSuggesting(false); }
  };

  const downloadZip = async () => {
    const zip = new JSZip();
    const used = new Set<string>();
    for (const s of shots) {
      let n = s.name, k = 2;
      while (used.has(n)) n = s.name.replace(/(\.[^.]+)$/, `-${k++}$1`);
      used.add(n); zip.file(n, s.blob);
    }
    downloadBlob(await zip.generateAsync({ type: "blob" }), `${file?.name.replace(/\.[^.]+$/, "") || "thumbnails"}_frames.zip`);
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" onFiles={handleFiles} label="Drop a video" description="Generate thumbnails from video frames" />
      ) : (
        <div className="space-y-4">
          {noPlayback ? (
            <NoticeBox className="text-sm">Your browser can't play this video (often iPhone HEVC video). You can still capture a frame: type the time below and press Capture. It takes a little longer because the frame is read by the video engine.</NoticeBox>
          ) : (
            <video ref={videoRef} src={preview} controls className="w-full max-h-64 rounded-lg border"
              onError={() => setNoPlayback(true)}
              onLoadedMetadata={(e) => { if (!e.currentTarget.videoWidth) setNoPlayback(true); }}
              onTimeUpdate={(e) => { if (document.activeElement?.id !== "thumb-time") setTimeText(fmtTime(e.currentTarget.currentTime)); }} />
          )}
          <p className="text-sm text-muted-foreground">Seek to the frame you want (use the step buttons for fine control), then click Capture. You can capture as many frames as you like.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" size="sm" onClick={() => step(-1)}>-1 s</Button>
            <Button variant="outline" size="sm" onClick={() => step(-1 / 30)}>-1 frame</Button>
            <Button variant="outline" size="sm" onClick={() => step(1 / 30)}>+1 frame</Button>
            <Button variant="outline" size="sm" onClick={() => step(1)}>+1 s</Button>
            <label className="flex items-center gap-1.5 text-sm">Time
              <input id="thumb-time" className="border rounded px-2 py-1 w-24 bg-background" value={timeText} onChange={(e) => setTimeText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { const t = parseTime(timeText); if (t >= 0) seekTo(t); else toast.error("Use a time like 1:05.5 or 65.5"); } }} aria-label="Time of the frame (mm:ss.ms), press Enter to jump" />
            </label>
            <label className="flex items-center gap-1.5 text-sm ml-2"><input type="checkbox" checked={asJpg} onChange={(e) => setAsJpg(e.target.checked)} /> Save as JPG (smaller)</label>
          </div>
          <div className="flex flex-wrap gap-3 items-center text-sm">
            <label className="flex items-center gap-1.5">Size
              <select className="border rounded px-2 py-1 bg-background" value={sizeId} onChange={(e) => setSizeId(e.target.value as typeof sizeId)}>
                {SIZES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </label>
            {sizeId !== "orig" && (
              <label className="flex items-center gap-1.5">Shape
                <select className="border rounded px-2 py-1 bg-background" value={fitMode} onChange={(e) => setFitMode(e.target.value as "fill" | "fit")}>
                  <option value="fill">Fill (crop the edges)</option><option value="fit">Fit (black bars)</option>
                </select>
              </label>
            )}
            <label className="flex items-center gap-1.5">Title (optional)
              <input className="border rounded px-2 py-1 bg-background w-56" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Text on the image" />
            </label>
            {title.trim() && (<>
              <label className="flex items-center gap-1.5">Font
                <select className="border rounded px-2 py-1 bg-background" value={fontId} onChange={(e) => setFontId(e.target.value as typeof fontId)}>{FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}</select>
              </label>
              <label className="flex items-center gap-1.5">Colour
                <select className="border rounded px-2 py-1 bg-background" value={titleColor} onChange={(e) => setTitleColor(e.target.value)}>{TITLE_COLORS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select>
              </label>
              <label className="flex items-center gap-1.5">Size
                <input type="range" min={5} max={16} value={titleScale} onChange={(e) => setTitleScale(Number(e.target.value))} aria-label="Title size" />
              </label>
            </>)}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={capture} disabled={busy}>{busy ? "Reading frame..." : "Capture Thumbnail"}</Button>
            {!noPlayback && <Button variant="outline" onClick={suggest} disabled={suggesting}>{suggesting ? "Looking..." : "Suggest frames"}</Button>}
            <Button variant="outline" onClick={() => { setFile(null); setPreview(""); setShots([]); setCandidates([]); }}>Reset</Button>
          </div>
          {candidates.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Click a frame to jump to it. The marked one is the sharpest, well-lit frame; dark or very bright frames are skipped.</p>
              <div className="grid grid-cols-5 gap-2">
                {candidates.map((c, i) => (
                  <button key={i} type="button" onClick={() => seekTo(c.t)} className={`relative rounded border overflow-hidden ${c.best ? "ring-2 ring-primary" : ""} ${c.score < 0 ? "opacity-40" : ""}`} title={`${fmtTime(c.t)}${c.best ? " (suggested)" : c.score < 0 ? " (too dark or bright)" : ""}`}>
                    <img src={c.url} alt={`Frame at ${fmtTime(c.t)}`} className="w-full" />
                    <span className="absolute bottom-0 left-0 bg-black/60 text-white text-[10px] px-1">{fmtTime(c.t)}{c.best ? " ★" : ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {shots.length > 1 && <Button variant="outline" size="sm" onClick={downloadZip}>Download all as ZIP ({shots.length})</Button>}
          {shots.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="thumb-gallery">
              {shots.map((s, i) => (
                <div key={i} className="space-y-1">
                  <img src={s.url} alt={`Frame at ${s.time.toFixed(1)}s`} className="rounded-lg border w-full" />
                  <p className="text-xs text-muted-foreground truncate">{s.name}</p>
                  <DownloadButton blob={s.blob} fileName={s.name} label="Download" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
